/**
 * PURE. Mesh accumulation for the art §3.2 formats.
 *
 * A *surface* is a small indexed patch (shared vertices) — one loft, one profile, one disc. Every triangle of a surface
 * becomes 3 non-indexed render vertices (flat facets, `corner` 0/1/2, per-triangle material/region/edgeMask) and, when
 * flagged, an indexed hull triangle on the surface's shared vertices (smooth normals accumulated per surface).
 * Parts (`beginPart`) record their render/hull ranges for the per-frame drag preview (creatures §A4.3).
 */
export type V3 = [number, number, number];

export interface Vert {
  p: V3;
  /** Distance along the part spine (raw H units; packed as u/2 H in unorm16). */
  u: number;
  /** Angle around the spine 0..1. */
  v: number;
  bA: number; bB: number;
  /** Weight of bone A, 0..1. */
  wA: number;
  /** Line weight 0..2 (art §5.8). */
  lw: number;
}

export interface TriOpts {
  mat: number;
  region: number;
  /** Bits 0–2: draw the edge opposite corner 0/1/2 (art §3.2). */
  edge?: number;
  /** Include in the inverted-hull outline mesh (default true). */
  hull?: boolean;
}

export class Surface {
  readonly verts: Vert[] = [];
  readonly tris: number[] = [];
  readonly triInfo: TriOpts[] = [];
  vert(v: Vert): number { this.verts.push(v); return this.verts.length - 1; }
  tri(a: number, b: number, c: number, o: TriOpts): void {
    if (a === b || b === c || a === c) return;
    this.tris.push(a, b, c);
    this.triInfo.push(o);
  }
  get triCount(): number { return this.tris.length / 3; }
}

export interface PartRange { name: string; renderTriStart: number; renderTriEnd: number; hullVertStart: number; hullVertEnd: number; hullIdxStart: number; hullIdxEnd: number }

/** Accumulates surfaces into flat render/hull arrays. */
export class MeshBuilder {
  // render (non-indexed)
  rPos: number[] = []; rU: number[] = []; rV: number[] = []; rSkin: number[] = []; rLw: number[] = []; rInfo: number[] = [];
  // hull (indexed)
  hPos: number[] = []; hNrm: number[] = []; hSkin: number[] = []; hLw: number[] = []; hIdx: number[] = [];
  parts: PartRange[] = [];
  private cur: PartRange | null = null;

  beginPart(name: string): void {
    this.endPart();
    this.cur = { name, renderTriStart: this.renderTris, renderTriEnd: 0, hullVertStart: this.hPos.length / 3, hullVertEnd: 0, hullIdxStart: this.hIdx.length, hullIdxEnd: 0 };
  }
  endPart(): void {
    if (!this.cur) return;
    this.cur.renderTriEnd = this.renderTris;
    this.cur.hullVertEnd = this.hPos.length / 3;
    this.cur.hullIdxEnd = this.hIdx.length;
    if (this.cur.renderTriEnd > this.cur.renderTriStart || this.cur.hullIdxEnd > this.cur.hullIdxStart) this.parts.push(this.cur);
    this.cur = null;
  }
  get renderTris(): number { return this.rPos.length / 9; }
  get hullTris(): number { return this.hIdx.length / 3; }

  /** Appends a surface. `region` of a hull vertex = the region of the first hull triangle using it. */
  add(s: Surface): void {
    const n = s.verts.length;
    // Render: 3 vertices per triangle.
    for (let t = 0; t < s.triCount; t++) {
      const info = s.triInfo[t];
      for (let c = 0; c < 3; c++) {
        const vtx = s.verts[s.tris[t * 3 + c]];
        this.rPos.push(vtx.p[0], vtx.p[1], vtx.p[2]);
        this.rU.push(vtx.u); this.rV.push(vtx.v);
        this.rSkin.push(vtx.bA, vtx.bB, vtx.wA);
        this.rLw.push(vtx.lw);
        this.rInfo.push(info.mat, info.region, info.edge ?? 0, c);
      }
    }
    // Hull: shared vertices of the hull triangles, smooth (area-weighted) normals.
    const used = new Int32Array(n).fill(-1);
    const nrm = new Float64Array(n * 3);
    const region = new Int32Array(n).fill(-1);
    const hullTris: number[] = [];
    for (let t = 0; t < s.triCount; t++) {
      if (s.triInfo[t].hull === false) continue;
      const a = s.tris[t * 3], b = s.tris[t * 3 + 1], c = s.tris[t * 3 + 2];
      const pa = s.verts[a].p, pb = s.verts[b].p, pc = s.verts[c].p;
      const ux = pb[0] - pa[0], uy = pb[1] - pa[1], uz = pb[2] - pa[2];
      const vx = pc[0] - pa[0], vy = pc[1] - pa[1], vz = pc[2] - pa[2];
      const nx = uy * vz - uz * vy, ny = uz * vx - ux * vz, nz = ux * vy - uy * vx;
      for (const i of [a, b, c]) {
        nrm[i * 3] += nx; nrm[i * 3 + 1] += ny; nrm[i * 3 + 2] += nz;
        if (region[i] < 0) region[i] = s.triInfo[t].region;
      }
      hullTris.push(a, b, c);
    }
    if (hullTris.length === 0) return;
    const base = this.hPos.length / 3;
    let next = base;
    for (const i of hullTris) {
      if (used[i] >= 0) continue;
      used[i] = next++;
      const vtx = s.verts[i];
      let nx = nrm[i * 3], ny = nrm[i * 3 + 1], nz = nrm[i * 3 + 2];
      const l = Math.hypot(nx, ny, nz);
      if (l > 1e-12) { nx /= l; ny /= l; nz /= l; } else { nx = 0; ny = 1; nz = 0; }
      this.hPos.push(vtx.p[0], vtx.p[1], vtx.p[2]);
      this.hNrm.push(nx, ny, nz);
      this.hSkin.push(vtx.bA, vtx.bB, vtx.wA, region[i] < 0 ? 0 : region[i]);
      this.hLw.push(vtx.lw);
    }
    for (const i of hullTris) this.hIdx.push(used[i]);
  }
}
